import { cmsWebComponentsMap } from '@ui/webComponentExports';
import { GeneratedColumn, GeneratedRow } from './contracts';
import { ColumnAlignment, ColumnDirection, GradientConfig } from '@repo/types';

const generateGradientCSS = (gradient: GradientConfig | undefined): string | undefined => {
  if (!gradient || gradient.stops.length < 2) return undefined;

  const sortedStops = [...gradient.stops].sort((a, b) => a.position - b.position);
  const stopsString = sortedStops.map((stop) => `${stop.color} ${stop.position}%`).join(', ');

  if (gradient.type === 'radial') {
    return `radial-gradient(circle, ${stopsString})`;
  }
  return `linear-gradient(${gradient.angle ?? 90}deg, ${stopsString})`;
};

export default function Row({ id, tree }: GeneratedRow) {
  const row = tree.rows[id]!;
  const rowStyle = row.style;

  const hasBackground = !!(rowStyle?.backgroundColor || rowStyle?.backgroundImage || rowStyle?.gradient);

  return (
    <div
      className={`slc-row${hasBackground ? ' slc-row--has-background' : ''}`}
      style={{
        backgroundColor: rowStyle?.gradient ? undefined : rowStyle?.backgroundColor || undefined,
        backgroundImage:
          generateGradientCSS(rowStyle?.gradient) ||
          (rowStyle?.backgroundImage ? `url(${rowStyle.backgroundImage})` : undefined),
        backgroundSize: rowStyle?.backgroundSize || undefined,
        backgroundPosition: rowStyle?.backgroundPosition || undefined,
        backgroundRepeat: rowStyle?.backgroundRepeat || undefined,
        minHeight: rowStyle?.height || undefined,
        maxHeight: rowStyle?.maxHeight || undefined,
      }}
    >
      {row.columnIds.map((columnId) => (
        <Column key={columnId} id={columnId} tree={tree} />
      ))}
    </div>
  );
}

function Column({ id, tree }: GeneratedColumn) {
  const column = tree.columns[id]!;
  const direction = column.direction ?? ColumnDirection.Bottom;
  const alignment = column.alignment ?? ColumnAlignment.Center;
  const columnStyle = column.style;

  const hasBackground = !!(columnStyle?.backgroundColor || columnStyle?.backgroundImage || columnStyle?.gradient);

  let justifyContent = 'center';
  if (alignment === ColumnAlignment.Left) {
    justifyContent = 'flex-start';
  } else if (alignment === ColumnAlignment.Right) {
    justifyContent = 'flex-end';
  }

  const columnContent = column.childrenIds.map((childId) => {
    if (tree.components[childId]) {
      const componentData = tree.components[childId];
      if (!componentData) {
        return null;
      }
      // @ts-expect-error TODO: fix this
      const Component = cmsWebComponentsMap[componentData.type];
      const props = {
        ...componentData,
        ...(componentData.data ?? {}),
      };

      // @ts-ignore
      return <Component key={childId} {...(props as any)} />;
    } else if (tree.rows[childId]) {
      return <Row key={childId} id={childId} tree={tree} />;
    }
  });

  // When fitToSiblings is enabled, use a wrapper structure so the column doesn't expand the row
  if (column.fitToSiblings) {
    return (
      <div
        key={id}
        className="slc-column-wrapper slc-column-wrapper--fit-to-siblings"
        style={{ gridColumn: `span ${column.size}` }}
      >
        <div
          className={`slc-column slc-column--fit-to-siblings${hasBackground ? ' slc-column--has-background' : ''}`}
          style={{
            flexDirection: direction === ColumnDirection.Bottom ? 'column' : 'row',
            justifyContent,
            alignItems: direction === ColumnDirection.Bottom ? justifyContent : 'center',
            backgroundColor: columnStyle?.gradient ? undefined : columnStyle?.backgroundColor || undefined,
            backgroundImage:
              generateGradientCSS(columnStyle?.gradient) ||
              (columnStyle?.backgroundImage ? `url(${columnStyle.backgroundImage})` : undefined),
            backgroundSize: columnStyle?.backgroundSize || undefined,
            backgroundPosition: columnStyle?.backgroundPosition || undefined,
            backgroundRepeat: columnStyle?.backgroundRepeat || undefined,
            minHeight: columnStyle?.height || undefined,
            maxHeight: columnStyle?.maxHeight || undefined,
          }}
        >
          {columnContent}
        </div>
      </div>
    );
  }

  return (
    <div
      key={id}
      className={`slc-column${hasBackground ? ' slc-column--has-background' : ''}`}
      style={{
        gridColumn: `span ${column.size}`,
        flexDirection: direction === ColumnDirection.Bottom ? 'column' : 'row',
        justifyContent,
        alignItems: direction === ColumnDirection.Bottom ? justifyContent : 'center',
        backgroundColor: columnStyle?.gradient ? undefined : columnStyle?.backgroundColor || undefined,
        backgroundImage:
          generateGradientCSS(columnStyle?.gradient) ||
          (columnStyle?.backgroundImage ? `url(${columnStyle.backgroundImage})` : undefined),
        backgroundSize: columnStyle?.backgroundSize || undefined,
        backgroundPosition: columnStyle?.backgroundPosition || undefined,
        backgroundRepeat: columnStyle?.backgroundRepeat || undefined,
        minHeight: columnStyle?.height || undefined,
        maxHeight: columnStyle?.maxHeight || undefined,
      }}
    >
      {columnContent}
    </div>
  );
}
